/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import {
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { EventType } from "../types";
import {
  eventLogOutputCheckboxes,
  QueryGetEvents,
  eventStatusCheckboxes,
  incomingEventStatusCheckboxes,
} from "./constants";

type DefinitionKey =
  | "eventStatuses"
  | "eventActions"
  | "eventRelatedPlans"
  | "eventLogOutputs";

type EventsQueryState = QueryGetEvents & QueryState;

type Definition = AppliedFilterDefinition<EventsQueryState, DefinitionKey>;

const eventStatusLabelsByValue = [
  ...eventStatusCheckboxes,
  ...incomingEventStatusCheckboxes,
].reduce<Record<string, string>>(
  (accumulator, statusOption) => ({
    ...accumulator,
    [statusOption.value]: statusOption.label,
  }),
  {}
);

const eventLogOutputLabelsByValue = eventLogOutputCheckboxes.reduce<
  Record<string, string>
>(
  (accumulator, logOutputOption) => ({
    ...accumulator,
    [logOutputOption.value]: logOutputOption.label,
  }),
  {}
);

export const createEventsAppliedFilters = (
  eventActionNamesById: Record<string, string> = {},
  eventRelatedPlanNamesById: Record<string, string> = {}
): Definition[] => [
  {
    key: "eventStatuses",
    label: "Event Status",
    getValues: (queryState) => queryState.eventStatuses ?? [],
    getValueLabel: (value) => eventStatusLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = eventStatusLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeEventStatuses ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeEventStatuses,
        {
          singular: "Event Status",
          plural: "Event Statuses",
        }
      );
    },
    clear: () => ({ eventStatuses: [], excludeEventStatuses: false }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.eventStatuses,
        valueToRemove
      );
      return {
        eventStatuses: nextValues,
        excludeEventStatuses: nextValues.length
          ? queryState.excludeEventStatuses
          : false,
      };
    },
  },
  {
    key: "eventActions",
    label: "Event Actions",
    getValues: (queryState) => queryState.eventActionIds ?? [],
    getValueLabel: (value) => eventActionNamesById[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = eventActionNamesById[values[0]] ?? values[0];
        return queryState.excludeEventActionIds ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeEventActionIds,
        {
          singular: "Event Action",
          plural: "Event Actions",
        }
      );
    },
    clear: () => ({ eventActionIds: [], excludeEventActionIds: false }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.eventActionIds,
        valueToRemove
      );
      return {
        eventActionIds: nextValues,
        excludeEventActionIds: nextValues.length
          ? queryState.excludeEventActionIds
          : false,
      };
    },
  },
  {
    key: "eventRelatedPlans",
    label: "Event Related Plan",
    getValues: (queryState) => queryState.eventRelatedPlanIds ?? [],
    getValueLabel: (value) => eventRelatedPlanNamesById[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = eventRelatedPlanNamesById[values[0]] ?? values[0];
        return queryState.excludeEventRelatedPlanIds
          ? `Exclude: ${label}`
          : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeEventRelatedPlanIds,
        {
          singular: "Event Related Plan",
          plural: "Event Related Plans",
        }
      );
    },
    clear: () => ({
      eventRelatedPlanIds: [],
      excludeEventRelatedPlanIds: false,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.eventRelatedPlanIds,
        valueToRemove
      );
      return {
        eventRelatedPlanIds: nextValues,
        excludeEventRelatedPlanIds: nextValues.length
          ? queryState.excludeEventRelatedPlanIds
          : false,
      };
    },
  },
  {
    key: "eventLogOutputs",
    label: "Log Output",
    getValueLabel: (value) => eventLogOutputLabelsByValue[value] ?? value,
    getValues: (queryState) =>
      queryState.eventsType === EventType.future
        ? []
        : queryState.eventLogOutputs ?? [],
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = eventLogOutputLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeEventLogOutputs ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeEventLogOutputs,
        {
          singular: "Log Output",
          plural: "Log Outputs",
        }
      );
    },
    clear: () => ({ eventLogOutputs: [], excludeEventLogOutputs: false }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.eventLogOutputs ?? [],
        valueToRemove
      );
      return {
        eventLogOutputs: nextValues,
        excludeEventLogOutputs: nextValues.length
          ? queryState.excludeEventLogOutputs
          : false,
      };
    },
  },
];
